import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  Alert,
} from '@mui/material';
import {
  AccountBalance as DepositIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  RemoveCircle as DeductionIcon,
} from '@mui/icons-material';
import { PageHeader, Loading, EmptyState } from '../../components/Layout';
import api from '../../services/api';

const TenantDeposits = () => {
  const [deposits, setDeposits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeposits();
    fetchTransactions();
  }, []);

  const fetchDeposits = async () => {
    try {
      setLoading(true);
      console.log('Fetching tenant deposits from API...');
      const response = await api.get('/api/deposits/me');
      console.log('Deposits response:', response.data);
      setDeposits(response.data || []);
    } catch (error) {
      console.error('Error loading deposits:', error);
      console.error('Error response:', error.response);
      if (error.response?.status === 403) {
        console.error('Permission denied - user may not be a tenant');
      }
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      console.log('Fetching deposit transactions...');
      const response = await api.get('/api/deposits/me/transactions');
      console.log('Transactions response:', response.data);
      setTransactions(response.data || []);
    } catch (error) {
      console.error('Error loading transactions:', error);
      setTransactions([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'held':
        return 'primary';
      case 'refunded':
        return 'success';
      case 'partially_used':
        return 'warning';
      case 'fully_used':
        return 'default';
      default:
        return 'default';
    }
  };

  const totalDeposits = deposits.reduce((sum, d) => sum + (d.amount || 0), 0);
  const currentBalance = deposits.reduce((sum, d) => sum + (d.remaining_amount || 0), 0);
  const totalDeducted = totalDeposits - currentBalance;

  if (loading) {
    return <Loading message="Loading deposits..." />;
  }

  return (
    <Box>
      <PageHeader
        title="My Security Deposits"
        subtitle="View your security deposit information"
        breadcrumbs={[{ label: 'Deposits' }]}
      />

      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        This page shows your security deposit information. For any questions or to request a
        refund, please contact your landlord.
      </Alert>

      {deposits.length === 0 ? (
        <EmptyState
          title="No Deposit Information"
          description="You don't have any security deposit records yet. Your deposit information will appear here once recorded by your landlord."
        />
      ) : (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Total Deposit
                      </Typography>
                      <Typography variant="h4">KES {totalDeposits.toLocaleString()}</Typography>
                    </Box>
                    <DepositIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Current Balance
                      </Typography>
                      <Typography variant="h4">KES {currentBalance.toLocaleString()}</Typography>
                    </Box>
                    <CheckIcon sx={{ fontSize: 48, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" variant="h6">
                        Total Deducted
                      </Typography>
                      <Typography variant="h4" color="error.main">
                        KES {totalDeducted.toLocaleString()}
                      </Typography>
                    </Box>
                    <DeductionIcon sx={{ fontSize: 48, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Deposits Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Payment Date</strong></TableCell>
                  <TableCell><strong>Amount (KES)</strong></TableCell>
                  <TableCell><strong>Remaining (KES)</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deposits.map((deposit) => (
                  <TableRow key={deposit.id}>
                    <TableCell>
                      {new Date(deposit.payment_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{deposit.amount?.toLocaleString()}</TableCell>
                    <TableCell>
                      {(deposit.remaining_amount || deposit.amount)?.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={deposit.status?.toUpperCase().replace('_', ' ')}
                        color={getStatusColor(deposit.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{deposit.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Transaction History */}
          {transactions.length > 0 && (
            <Paper sx={{ mt: 3, p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Deduction History
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Details of all deductions, transfers, and refunds from your security deposit
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Amount (KES)</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {new Date(transaction.transaction_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.transaction_type.replace('_', ' ').toUpperCase()}
                            color={
                              transaction.transaction_type === 'refund'
                                ? 'success'
                                : transaction.transaction_type === 'deduction'
                                ? 'error'
                                : 'warning'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            color={
                              transaction.transaction_type === 'refund'
                                ? 'success.main'
                                : 'error.main'
                            }
                          >
                            {transaction.transaction_type === 'refund' ? '+' : '-'}
                            {transaction.amount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.reason || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Information Box */}
          <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.light' }}>
            <Typography variant="h6" gutterBottom>
              About Your Security Deposit
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Your security deposit is held by your landlord and will be refunded at the end of
              your tenancy if there are no damages.
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Deductions may be made for damages, unpaid rent, or other charges as per your lease
              agreement.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Contact your landlord for any questions about your deposit or to request a refund
              when vacating.
            </Typography>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default TenantDeposits;
